import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  enableVolumeMaintenance,
  listStorageServiceDetails,
} from 'app/plugins/infrastructure/components/common/service-health/action'
import { allStorageServicesSelector } from 'app/plugins/infrastructure/components/common/service-health/selector'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import SimpleLink from 'core/components/SimpleLink'
import PicklistField from 'core/components/validatedForm/DropdownField'
import Badge from 'core/elements/badge/Badge'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import Grid from 'core/elements/grid/Grid'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import type Theme from 'core/themes/model'
import { getAllVolumesWithParams } from '../../../storage/volumes/actions'
import { initiateVolumeHostTransfer } from '../../actions'
import {
  attachTransferResults,
  type DryRunState,
  runVolumeTransferDryRun,
  volumeListColumnsWithResult,
} from '../EnableMaintenanceModeDialog'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: theme.spacing(2),
    display: 'grid',
  },
  backendsTable: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    gap: theme.spacing(1),
  },
  // One backend's cells as a subgrid so its columns line up with the header and other
  // backends, with a line above it (above the first backend, and between backends).
  backendRow: {
    gridColumn: '1 / -1',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    gap: 8,
    paddingTop: 8,
    borderTop: `1px solid ${theme.components.table.border}`,
  },
  backend: {
    display: 'grid',
    gap: 4,
    justifyItems: 'start',
  },
  volumeList: {
    // Span the whole row of the backends grid
    gridColumn: '1 / -1',
    overflow: 'auto',
    marginTop: theme.spacing(1),
    // Same border color/width the Grid uses for its container (components.table.border)
    borderLeft: `1px solid ${theme.components.table.border}`,
    borderRight: `1px solid ${theme.components.table.border}`,
  },
  hostCode: {
    fontFamily: 'SpaceMono',
    fontSize: 13,
    wordBreak: 'break-all',
  },
  verticalCenter: {
    alignSelf: 'center',
  },
  errorText: {
    cursor: 'pointer',
  },
  errorIcon: {
    color: theme.palette.red[500],
    cursor: 'pointer',
  },
}))

// Picklist value for "leave this backend's volumes where they are" (non-maintenance only)
const DO_NOT_TRANSFER = 'do-not-transfer'

interface Props {
  // Grid dialog contract: the host to transfer volumes from is the only row
  rows: any[]
  onClose: (success?: boolean) => void
  // true: maintenance version (retrying after storage maintenance; targets required, services
  // disabled before transferring). false: standard version (targets optional, nothing disabled).
  maintenance: boolean
  // Maintenance version only; the standard version fetches the host's volumes itself
  remainingVolumes?: any[]
}

// See VolumeTransfersModal.spec.md for VTM-FR-001 through VTM-FR-012
export default function VolumeTransfersModal({
  rows,
  onClose,
  maintenance,
  remainingVolumes,
}: Props) {
  const host = rows?.[0]
  const classes = useStyles()
  const [volumesByCinderService, setVolumesByCinderService] = useState({})
  const [loadingVolumes, setLoadingVolumes] = useState(!maintenance)
  const [expandedBackends, setExpandedBackends] = useState<Record<string, boolean>>({})
  const [submittingStorage, setSubmittingStorage] = useState(false)
  const [transferErrorsByService, setTransferErrorsByService] = useState({})
  // Dry-run transfer outcome per source service host, shown in the volume grid's Result column
  const [dryRunResults, setDryRunResults] = useState<Record<string, DryRunState>>({})
  // Latest target requested per source service host, to drop stale dry-run responses
  const latestDryRunTarget = useRef<Record<string, string>>({})

  const defaultParams = {
    // Target service host (host@backend) to transfer to, keyed by the source service host
    transferTargets: {} as Record<string, string>,
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { loading: loadingStorageServices } = useListAction(listStorageServiceDetails)
  const storageServices = useSelector(allStorageServicesSelector)

  const {
    update: disableStorage,
    updating: disablingStorage,
    error: errorDisablingStorage,
    reset: resetDisableStorage,
  } = useUpdateAction(enableVolumeMaintenance)

  // VTM-FR-003: Transfer target options limited to other scheduling-enabled hosts on the same backend
  // Other hosts running the same storage backend as this service (matched on
  // volumeBackend), excluding the host being put into maintenance.
  // This one is different from enableMaintenanceMode, host object
  // has the raw service instead of one processed via selector
  const getTransferTargetOptions = useCallback(
    (service) =>
      (storageServices || [])
        .filter(
          (candidate) =>
            candidate?.volumeBackend === service?.host?.split('@')?.[1] &&
            candidate?.schedulingStatus === 'enabled' &&
            !candidate?.host?.includes(host?.id),
        )
        .map((candidate) => ({
          label:
            candidate?.hostDetails?.name ||
            candidate?.hostDetails?.info?.hostname ||
            candidate?.host,
          value: candidate?.host,
        })),
    [storageServices, host],
  )

  // VTM-FR-002, VTM-FR-011: Volumes grouped by their backend service host. The maintenance
  // version is handed the remaining volumes; the standard version fetches all of the host's.
  useEffect(() => {
    const groupByService = (volumes: any[]) =>
      volumes.reduce((accum, volume) => {
        const volumeBackend = volume?.['os-vol-host-attr:host']?.split('#')?.[0]
        if (accum[volumeBackend]) {
          accum[volumeBackend].push(volume)
        } else {
          accum[volumeBackend] = [volume]
        }
        return accum
      }, {})

    if (maintenance) {
      setVolumesByCinderService(groupByService(remainingVolumes || []))
      return undefined
    }
    let cancelled = false
    const fetchVolumes = async () => {
      setLoadingVolumes(true)
      const allVolumes = await getAllVolumesWithParams({ filters: { host_id: host?.id } })
      if (cancelled) return
      setVolumesByCinderService(groupByService(allVolumes || []))
      setLoadingVolumes(false)
    }
    fetchVolumes()
    return () => {
      cancelled = true
    }
  }, [maintenance, remainingVolumes, host])

  // Choosing a transfer target runs a dry-run transfer for that source -> target pair and
  // expands the volume list so the per-volume result is visible right away
  // VTM-FR-008: "Do not transfer" clears the target and the volumes' results, with no dry run
  const handleTransferTargetChange = useCallback(
    async (service, targetHostId) => {
      const sourceHostId: string = service?.host
      latestDryRunTarget.current[sourceHostId] = targetHostId
      if (targetHostId === DO_NOT_TRANSFER) {
        const { [sourceHostId]: _removed, ...remainingTargets } = params.transferTargets
        setParams({ ...params, transferTargets: remainingTargets })
        setDryRunResults((prev) => {
          const { [sourceHostId]: _cleared, ...rest } = prev
          return rest
        })
        return
      }
      updateParams({
        transferTargets: { ...params.transferTargets, [sourceHostId]: targetHostId },
      })
      if (volumesByCinderService[sourceHostId]?.length) {
        setExpandedBackends((prev) => ({ ...prev, [sourceHostId]: true }))
      }
      setDryRunResults((prev) => ({ ...prev, [sourceHostId]: { loading: true } }))
      const result = await runVolumeTransferDryRun(sourceHostId, targetHostId)
      // The target changed while this dry run was in flight
      if (latestDryRunTarget.current[sourceHostId] !== targetHostId) return
      setDryRunResults((prev) => ({ ...prev, [sourceHostId]: result }))
    },
    [params, setParams, updateParams, volumesByCinderService],
  )

  // VTM-FR-005: Disable a storage service with a maintenance:<timestamp> reason
  const handleVolumeMaintenanceToggle = useCallback(
    async (serviceId) => {
      const timestamp = Date.now()
      const body = {
        host: serviceId,
        binary: 'cinder-volume',
        disabled_reason: `maintenance:${timestamp}`,
      }
      return await disableStorage({ body })
    },
    [host, disableStorage],
  )

  // VTM-FR-005, VTM-FR-006, VTM-FR-007, VTM-FR-008: Disable services, transfer volumes to the
  // chosen targets, stay open with per-backend errors on failure, otherwise close
  const handleSubmit = useCallback(async () => {
    setSubmittingStorage(true)
    let transferFail = false
    const errorsByService = {}
    await Promise.allSettled(
      host?.storageServices.map(async (service) => {
        // Maintenance version: disable the service if it is not in maintenance mode already
        if (maintenance && !service?.disabled_reason?.includes('maintenance')) {
          const disableResult = await handleVolumeMaintenanceToggle(service?.host)
          if (!disableResult?.success) {
            return false
          }
        }
        // Both ids are the full service ids (host@backend)
        const targetHostId = params.transferTargets?.[service?.host]
        if (targetHostId) {
          const response = await initiateVolumeHostTransfer({
            sourceHostId: service?.host,
            targetHostId,
            dryRun: false,
          })
          if (response?.volume_host_transfer?.status === 'fail') {
            transferFail = true
            errorsByService[service?.host] = response?.volume_host_transfer?.message
          }
        }
        return true
      }),
    )
    if (transferFail) {
      setTransferErrorsByService(errorsByService)
      setSubmittingStorage(false)
      return
    }
    setSubmittingStorage(false)
    handleClose(true)
  }, [params, host, maintenance])

  // VTM-FR-009: Closing resets the disable action state before calling onClose
  const handleClose = (success?: boolean) => {
    resetDisableStorage()
    onClose(success)
  }

  return (
    <ModalForm
      title={maintenance ? 'Retry Volume Transfers' : 'Transfer Volumes'}
      onSubmit={handleSubmit}
      onClose={() => handleClose()}
      loading={loadingStorageServices || loadingVolumes}
      submitting={disablingStorage || submittingStorage}
      error={errorDisablingStorage}
      submitTitle={maintenance ? 'Retry Volume Transfers' : 'Transfer Volumes'}
      panel="drawer"
      maxWidth={1200}
      open
    >
      <div className={classes.fields}>
        <Text variant="caption1">Select a target host for each backend.</Text>
        <div className={classes.backendsTable}>
          <Text variant="caption1">Backend</Text>
          <Text variant="caption1">Volumes</Text>
          <Text variant="caption1">Transfer To</Text>
          {host?.storageServices?.map((service) => (
            <div key={service.id} className={classes.backendRow}>
              <div className={classes.backend}>
                <Text variant="caption1">{host?.name}</Text>
                <Text variant="body2" className={classes.hostCode}>
                  {service?.host}
                </Text>
                {transferErrorsByService[service?.host] && (
                  <Tooltip message={transferErrorsByService[service?.host]}>
                    <Text variant="body2" className={classes.errorText}>
                      <FontAwesomeIcon className={classes.errorIcon} solid>
                        circle-xmark
                      </FontAwesomeIcon>{' '}
                      Transfer Failed
                    </Text>
                  </Tooltip>
                )}
              </div>
              <div className={classes.verticalCenter}>
                {volumesByCinderService[service?.host]?.length ? (
                  <SimpleLink
                    icon={expandedBackends[service?.host] ? 'chevron-down' : 'chevron-right'}
                    iconPosition="left"
                    onClick={() =>
                      setExpandedBackends((prev) => ({
                        ...prev,
                        [service?.host]: !prev[service?.host],
                      }))
                    }
                  >
                    {volumesByCinderService[service?.host].length} volumes
                  </SimpleLink>
                ) : (
                  <Text variant="body2">0 volumes</Text>
                )}
              </div>
              {getTransferTargetOptions(service).length ? (
                <PicklistField
                  id={`transferTarget-${service?.host}`}
                  label=""
                  DropdownComponent={AsyncDropdown}
                  items={
                    maintenance
                      ? getTransferTargetOptions(service)
                      : [
                          { label: 'Do not transfer', value: DO_NOT_TRANSFER },
                          ...getTransferTargetOptions(service),
                        ]
                  }
                  value={
                    params.transferTargets?.[service?.host] ??
                    (maintenance ? undefined : DO_NOT_TRANSFER)
                  }
                  onChange={(value) => handleTransferTargetChange(service, value)}
                  required={maintenance}
                />
              ) : (
                <div className={classes.verticalCenter}>
                  <Badge variant="error" text="No Eligible Host" ellipsisAt={25} />
                </div>
              )}
              {expandedBackends[service?.host] && (
                <div className={classes.volumeList}>
                  <Grid
                    uniqueIdentifier="id"
                    data={attachTransferResults(
                      volumesByCinderService[service?.host],
                      dryRunResults[service?.host],
                    )}
                    columns={volumeListColumnsWithResult}
                    loading={false}
                    compact
                    disableToolbar
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </ModalForm>
  )
}
